import React, { createContext, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { io } from 'socket.io-client';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { API_CONFIG } from './utils/apiConfig';
import './utils/forceLocalhostAPI';
import CORSFix from './components/CORSFix/CORSFix';
import Logout from './components/Auth/LogIn/Logout.js';
import CashBill from './components/DashBoard/Sidebar/Cashbill/CashBill.js';
import CreditNote from './components/DashBoard/Sidebar/CreditNote/CreditNote.js';
import DebitNote from './components/DashBoard/Sidebar/DebitNote/DebitNote.js';
import CreditBill from './components/DashBoard/Sidebar/CreditBill/CreditBill.js';
import PaymentReceipt from './components/DashBoard/Sidebar/PaymentReceipt/PaymentReceipt.js';
import Login from './components/Auth/LogIn/Login.js';
import Main from './Mainpage/Main/Main.js';
import Register from './components/Auth/Register/Register.js';
import ForgotPassword from './components/Auth/ForgotPassword/ForgotPassword.js';
import Dashboard from './components/DashBoard/Dashboard.js';
import ManageCustomers from './components/DashBoard/Customer/ManageCustomers.js';
import SuperAdminPanel from './components/SuperAdmin/SuperAdminPanel/SuperAdminPanel.js';
import AccountsAdminPanel from './components/SuperAdmin/AccountsAdmin/AccountsAdminPanel.js';
import HRAdminPanel from './components/SuperAdmin/HRAdmin/HRAdminPanel.js';
import MarketingAdminPanel from './components/SuperAdmin/MarketingAdmin/MarketingAdminPanel.js';
import DigitalMarketingAdminPanel from './components/SuperAdmin/DigitalMarketing/DigitalMarketingAdminPanel.js';
import PurchasingAdminPanel from './components/SuperAdmin/PurchasingAdminPanel/PurchasingAdminPanel.js';
import BillSummary from './components/SuperAdmin/AccountsAdmin/BillSummary.js';
import BillDistribution from './components/SuperAdmin/AccountsAdmin/BillDistribution.js';
import PaymentHistory from './components/SuperAdmin/AccountsAdmin/PaymentHistory.js';
import StaffList from './components/SuperAdmin/AccountsAdmin/StaffList.js';
import PriceListView from './components/SuperAdmin/PurchasingAdminPanel/PriceListView.js';
import AddPriceList from './components/SuperAdmin/PurchasingAdminPanel/AddPriceList.js';
import QuotationForm from './components/DashBoard/Sidebar/QuotationForm/QuotationForm.js';
import CompanyHeader from './components/DashBoard/Sidebar/CompanyHeader/CompanyHeader.js';
import ResetPasswordPage from './components/SuperAdmin/SuperAdminPanel/ResetPasswordPage.js';
import PWAInstallPrompt from './components/PWAInstallPrompt.js';
import URLProtection from './components/Security/URLProtection.js';
export const SocketContext = createContext();

const PathValidator = ({ children }) => {
  return children;
};

function App() {
  const socketRef = useRef(null);

useEffect(() => {
  const socketURL = API_CONFIG.WEBSOCKET_URL;
  
  if (socketURL && process.env.NODE_ENV === 'development') {
    socketRef.current = io(socketURL, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }
}, []);

  // Determine basename based on environment
  const basename = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? '' 
    : '/wyenfos-bills';

  return (
  <AuthProvider>
    <SocketContext.Provider value={socketRef.current}>
      <PathValidator>
        <Router basename={basename}>
          <URLProtection>
            <div className="App">
              <CORSFix />
              <Routes>
              <Route path="/" element={<Main />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/logout" element={<Logout />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } />
              
              <Route path="/customers" element={
                <ProtectedRoute>
                  <ManageCustomers />
                </ProtectedRoute>
              } />
              
              <Route path="/cash-bill" element={
                <ProtectedRoute>
                  <CashBill />
                </ProtectedRoute>
              } />
              
              <Route path="/credit-bill" element={
                <ProtectedRoute>
                  <CreditBill />
                </ProtectedRoute>
              } />
              
              <Route path="/credit-note" element={
                <ProtectedRoute>
                  <CreditNote />
                </ProtectedRoute>
              } />
              
              <Route path="/debit-note" element={
                <ProtectedRoute>
                  <DebitNote />
                </ProtectedRoute>
              } />
              
              <Route path="/payment-receipt" element={
                <ProtectedRoute>
                  <PaymentReceipt />
                </ProtectedRoute>
              } />
              
              <Route path="/quotation" element={
                <ProtectedRoute>
                  <QuotationForm />
                </ProtectedRoute>
              } />
              
              <Route path="/company-header" element={
                <ProtectedRoute>
                  <CompanyHeader />
                </ProtectedRoute>
              } />
              
              {/* Admin Routes */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AccountsAdminPanel />
                </AdminRoute>
              } />
              
              {/* Accounts Admin Routes */}
              <Route path="/billing-details" element={
                <AdminRoute department="accounts">
                  <BillSummary />
                </AdminRoute>
              } />
              
              <Route path="/bill-distribution" element={
                <AdminRoute department="accounts">
                  <BillDistribution />
                </AdminRoute>
              } />
              
              <Route path="/payment-history" element={
                <AdminRoute department="accounts">
                  <PaymentHistory />
                </AdminRoute>
              } />
              
              <Route path="/staff-list" element={
                <AdminRoute>
                  <StaffList />
                </AdminRoute>
              } />
              
              <Route path="/hr-admin" element={
                <AdminRoute department="hr">
                  <HRAdminPanel />
                </AdminRoute>
              } />
              
              <Route path="/marketing-admin" element={
                <AdminRoute department="marketing">
                  <MarketingAdminPanel />
                </AdminRoute>
              } />
              
              <Route path="/digital-marketing-admin" element={
                <AdminRoute department="digital-marketing">
                  <DigitalMarketingAdminPanel />
                </AdminRoute>
              } />
              
              <Route path="/purchasing-admin/price-lists" element={
                <AdminRoute department="purchase">
                  <PriceListView />
                </AdminRoute>
              } />
              
              <Route path="/purchasing-admin/add-price-list" element={
                <AdminRoute department="purchase">
                  <AddPriceList />
                </AdminRoute>
              } />
              
              <Route path="/purchasing-admin" element={
                <AdminRoute department="purchase">
                  <PurchasingAdminPanel />
                </AdminRoute>
              } />
              
              <Route path="/purchasing-admin/*" element={
                <AdminRoute department="purchase">
                  <PurchasingAdminPanel />
                </AdminRoute>
              } />
              
              {/* Super Admin Routes */}
              <Route path="/super-admin/*" element={
                <SuperAdminRoute>
                  <SuperAdminPanel />
                </SuperAdminRoute>
              } />
              
              <Route path="/reset-password-page" element={
                <SuperAdminRoute>
                  <ResetPasswordPage/>
                </SuperAdminRoute>
              } />
              
              {/* Catch all route - redirect to dashboard for authenticated users, login for others */}
              <Route path="*" element={<CatchAllRoute />} />
              </Routes>
            </div>
          </URLProtection>
        </Router>
      </PathValidator>
      <PWAInstallPrompt />
    </SocketContext.Provider>
  </AuthProvider>
);
}

const ProtectedRoute = ({ children, department }) => {
  const { currentUser } = useAuth();
  const userDept = localStorage.getItem('dept');

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (department && userDept !== department) {
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

const AdminRoute = ({ children, department }) => {
  const { currentUser, userProfile, loading } = useAuth();

  // Get cached role and department as fallback
  const cachedRole = localStorage.getItem('userRole');
  const cachedDepartment = localStorage.getItem('userDepartment');
  const effectiveRole = userProfile?.role || cachedRole;
  const effectiveDepartment = userProfile?.department || cachedDepartment;

  console.log('🔍 AdminRoute Debug:', {
    department: department,
    currentUser: currentUser ? { uid: currentUser.uid, email: currentUser.email } : null,
    userProfile: userProfile,
    effectiveRole: effectiveRole,
    effectiveDepartment: effectiveDepartment,
    cachedRole: cachedRole,
    cachedDepartment: cachedDepartment,
    path: window.location.pathname,
    loading: loading
  });

  // Show loading while authentication is being determined
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  if (!currentUser) {
    console.log('🔍 AdminRoute: No current user, redirecting to login');
    return <Navigate to="/login" />;
  }

  // If we don't have a role yet, wait a bit more for profile to load
  if (!effectiveRole) {
    console.log('🔍 AdminRoute: No role found, waiting for profile...');
    console.log('🔍 All localStorage keys:', Object.keys(localStorage));
    console.log('🔍 Current user email:', currentUser.email);
    
    // Try to get role from localStorage with different keys
    const altRole = localStorage.getItem('role');
    const userEmail = localStorage.getItem('userEmail');
    const email = localStorage.getItem('email');
    
    console.log('🔍 Found localStorage values:', { altRole, userEmail, email });
    
    // For HR Admin users, set default admin role
    if (currentUser.email && currentUser.email.includes('admin')) {
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('userDepartment', 'hr');
      console.log('🔍 AdminRoute: Set default admin role for HR user');
      
      // Force reload the component to pick up the new role
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
    
    return <div style={{ padding: '20px', textAlign: 'center' }}>
      <div>Loading user profile...</div>
      <div style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
        Debug: Email: {currentUser.email} | Role: {effectiveRole || 'None'}
      </div>
    </div>;
  }

  if (effectiveRole !== 'admin' && 
      effectiveRole !== 'super_admin' && 
      effectiveRole !== 'superadmin' &&
      effectiveRole !== 'purchase_admin' &&
      effectiveRole !== 'purchaseadmin' &&
      effectiveRole !== 'marketing_admin' &&
      effectiveRole !== 'marketingadmin' &&
      effectiveRole !== 'hr_admin' &&
      effectiveRole !== 'hradmin' &&
      effectiveRole !== 'digital_marketing_admin' &&
      effectiveRole !== 'digitalmarketingadmin') {
    console.log('🔍 AdminRoute: Invalid role:', effectiveRole);
    return <Navigate to="/unauthorized" />;
  }

  if (department && effectiveDepartment) {
    // Normalize department names for comparison
    const userDept = effectiveDepartment.toLowerCase().replace(/[ _]+/g, '-').trim();
    const expectedDept = department.toLowerCase().replace(/[ _]+/g, '-').trim();
    
    console.log('🔍 AdminRoute Department Check:', { userDept, expectedDept });
    
    // More flexible department matching
    const isDepartmentMatch = userDept === expectedDept || 
                             userDept.includes(expectedDept) || 
                             expectedDept.includes(userDept) ||
                             (expectedDept === 'marketing' && (userDept === 'admin' || userDept === 'marketing-admin')) ||
                             // Allow HR admins to access accounts features
                             (expectedDept === 'accounts' && userDept === 'hr') ||
                             // Allow accounts admins to access HR features
                             (expectedDept === 'hr' && userDept === 'accounts');
    
    if (!isDepartmentMatch) {
      console.log('🔍 AdminRoute: Department mismatch:', { userDept, expectedDept, userProfile });
      return <Navigate to="/unauthorized" />;
    }
  }

  console.log('🔍 AdminRoute: Access granted');
  return children;
};

const SuperAdminRoute = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Loading...</div>
    </div>;
  }

  if (!currentUser) {
    console.log('🔍 SuperAdminRoute: No current user, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Check role from localStorage as fallback if userProfile is not loaded yet
  const roleFromStorage = localStorage.getItem('role')?.toLowerCase().replace(/[ _]/g, '') || '';
  
  if (!userProfile && roleFromStorage !== 'superadmin' && roleFromStorage !== 'super_admin') {
    console.log('🔍 SuperAdminRoute: No profile and role mismatch, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  if (userProfile && userProfile?.role !== 'superadmin' && userProfile?.role !== 'super_admin') {
    console.log('🔍 SuperAdminRoute: Role mismatch, redirecting to unauthorized');
    return <Navigate to="/unauthorized" replace />;
  }

  // If we have a current user and either:
  // 1. userProfile with correct role, OR
  // 2. no userProfile but localStorage has correct role
  if (currentUser && (
    (userProfile && (userProfile.role === 'superadmin' || userProfile.role === 'super_admin')) ||
    (!userProfile && (roleFromStorage === 'superadmin' || roleFromStorage === 'super_admin'))
  )) {
    return children;
  }

  // Default fallback - show loading while things are being resolved
  return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div>Verifying access...</div>
  </div>;
};

const CatchAllRoute = () => {
  const { currentUser, loading } = useAuth();
  const location = useLocation();

  // Show loading while auth is being checked
  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div>Loading...</div>
    </div>;
  }

  if (!currentUser) {
    console.log('🔍 CatchAllRoute: No current user, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  const normalizedRole = localStorage.getItem('role')?.toLowerCase().replace(/[ _]/g, '') || '';
  const normalizedDept = localStorage.getItem('dept')?.toLowerCase().replace(/[ _]+/g, '-').trim() || '';

  let targetRoute = '/dashboard'; // Default to staff dashboard

  if (normalizedRole === 'superadmin' || normalizedRole === 'super_admin') {
    targetRoute = '/super-admin/dashboard';
  } else if (normalizedRole === 'admin' || normalizedRole === 'purchaseadmin' || normalizedRole === 'purchase_admin') {
    switch (normalizedDept) {
      case 'accounts':
        targetRoute = '/admin';
        break;
      case 'hr':
        targetRoute = '/hr-admin';
        break;
      case 'marketing':
        targetRoute = '/marketing-admin';
        break;
      case 'digital-marketing':
        targetRoute = '/digital-marketing-admin';
        break;
      case 'purchase':
      case 'purchasing':
        targetRoute = '/purchasing-admin';
        break;
      default:
        targetRoute = '/dashboard';
    }
  }

  if (location.pathname === targetRoute) {
    console.log('🔍 CatchAllRoute: Already on target route:', targetRoute);
    return null;
  }

  console.log('🔍 CatchAllRoute: Redirecting to', targetRoute, 'for role:', normalizedRole, 'dept:', normalizedDept);
  return <Navigate to={targetRoute} replace />;
};

export default App;